import { type PropsWithChildren, ReactNode, useRef } from "react";
import cn from "classnames";
import styled from "@emotion/styled";
import { useScrollHeight } from "src/hooks/useScrollHeight";

const StyledExpandable = styled.div`
  overflow: hidden;
  max-height: 0;
  transition: max-height 0.3s, top 0.3s;
  position: relative;

  &:after {
    content: "";
    background: linear-gradient(0, #ffffff 0%, rgba(255, 255, 255, 0) 100%);
    position: absolute;
    width: 100%;
    bottom: 0;
    height: 48px;
    display: none;

    .expanded,
    .always-visible {
      display: block;
    }
  }
`;

type ExpandableProps = {
  className?: string;
  isExpanded?: boolean;
  children: ReactNode;
  isUpward?: boolean;
  minHeight?: number;
  id?: string;
} & PropsWithChildren;

function Expandable({
  id,
  className,
  isExpanded,
  minHeight = 0,
  isUpward,
  children,
}: ExpandableProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const scrollHeight = useScrollHeight(contentRef, [children]);

  return (
    <StyledExpandable
      id={id}
      data-testid="expandable"
      ref={contentRef}
      className={cn(className, {
        Expandable_expanded: isExpanded,
        ["expanded"]: isExpanded,
        ["always-visible"]: minHeight > 0,
      })}
      style={{
        maxHeight: isExpanded ? scrollHeight : minHeight,
        visibility: isExpanded || minHeight > 0 ? "visible" : "hidden",
        ...(isUpward ? { top: -28 - (isExpanded ? scrollHeight : 0) } : {}),
      }}
    >
      {children}
    </StyledExpandable>
  );
}

export { Expandable };
